/**
 * Enhanced Android Connectivity Service
 * Provides robust network handling with retry logic, connection pooling, and error recovery
 */

import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { getNetworkStatus } from '../utils/capacitorNetwork';
import api from './api';

class ConnectivityService {
  constructor() {
    this.isOnline = true;
    this.connectionType = 'unknown';
    this.retryAttempts = 3;
    this.retryDelay = 1000; // Start with 1 second
    this.maxRetryDelay = 30000; // Max 30 seconds
    this.listeners = [];
    this.connectionCheckInterval = null;
    this.lastSuccessfulConnection = Date.now();
    this.consecutiveFailures = 0;
  }

  /**
   * Initialize connectivity monitoring
   * Works in both native app and web browser
   */
  async initialize() {
    const isNative = Capacitor.isNativePlatform();
    
    console.log(`ConnectivityService: Initializing in ${isNative ? 'native' : 'web'} mode...`);

    // Get initial status
    await this.updateConnectionStatus();

    if (isNative) {
      // Native mode: Use Capacitor Network plugin
      // Listen to network changes
      Network.addListener('networkStatusChange', async (status) => {
        console.log('Network status changed:', status);
        await this.updateConnectionStatus();
        this.notifyListeners();
      });
    } else {
      // Web mode: Use browser APIs
      window.addEventListener('online', async () => {
        console.log('Browser: Network online');
        await this.updateConnectionStatus();
        this.notifyListeners();
      });

      window.addEventListener('offline', async () => {
        console.log('Browser: Network offline');
        await this.updateConnectionStatus();
        this.notifyListeners();
      });

      // Also listen to connection changes if available
      if (navigator.connection || navigator.mozConnection || navigator.webkitConnection) {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        connection.addEventListener('change', async () => {
          await this.updateConnectionStatus();
          this.notifyListeners();
        });
      }
    }

    // Periodic connection health check (works in both modes)
    this.startHealthCheck();

    // Monitor API connectivity (works in both modes)
    this.monitorApiConnectivity();
  }

  /**
   * Update connection status
   */
  async updateConnectionStatus() {
    try {
      const status = await getNetworkStatus();
      const wasOnline = this.isOnline;
      this.isOnline = status.connected;
      this.connectionType = status.connectionType;

      if (wasOnline !== this.isOnline) {
        console.log(`Connectivity changed: ${wasOnline ? 'ONLINE' : 'OFFLINE'} → ${this.isOnline ? 'ONLINE' : 'OFFLINE'}`);
        
        if (this.isOnline) {
          this.consecutiveFailures = 0;
          this.lastSuccessfulConnection = Date.now();
          // Trigger sync when back online
          this.triggerReconnect();
        }
      }
    } catch (error) {
      console.error('Error updating connection status:', error);
    }
  }

  /**
   * Start periodic health check
   */
  startHealthCheck() {
    if (this.connectionCheckInterval) {
      clearInterval(this.connectionCheckInterval);
    }

    // Check every 10 seconds
    this.connectionCheckInterval = setInterval(async () => {
      await this.updateConnectionStatus();
      
      // If offline for more than 5 minutes, try to reconnect
      if (!this.isOnline && Date.now() - this.lastSuccessfulConnection > 300000) {
        console.log('Offline for 5+ minutes, attempting reconnection...');
        await this.attemptReconnection();
      }
    }, 10000);
  }

  /**
   * Monitor API connectivity with health checks
   */
  async monitorApiConnectivity() {
    setInterval(async () => {
      if (this.isOnline) {
        try {
          // Lightweight health check
          const response = await fetch(`${api.defaults.baseURL}/health/`, {
            method: 'GET',
            signal: AbortSignal.timeout(5000), // 5 second timeout
          });
          
          if (response.ok) {
            this.consecutiveFailures = 0;
            this.lastSuccessfulConnection = Date.now();
          } else {
            this.consecutiveFailures++;
          }
        } catch (error) {
          this.consecutiveFailures++;
          console.warn('API health check failed:', error.message);
          
          // If multiple failures, mark as offline
          if (this.consecutiveFailures >= 3) {
            console.warn('Multiple API failures, treating as offline');
            this.isOnline = false;
            this.notifyListeners();
          }
        }
      }
    }, 30000); // Check every 30 seconds
  }

  /**
   * Attempt to reconnect
   */
  async attemptReconnection() {
    console.log('Attempting reconnection...');
    
    for (let attempt = 1; attempt <= this.retryAttempts; attempt++) {
      try {
        await this.updateConnectionStatus();
        
        if (this.isOnline) {
          // Test API connectivity
          const response = await fetch(`${api.defaults.baseURL}/health/`, {
            method: 'GET',
            signal: AbortSignal.timeout(5000),
          });
          
          if (response.ok) {
            console.log('Reconnection successful!');
            this.consecutiveFailures = 0;
            this.lastSuccessfulConnection = Date.now();
            this.notifyListeners();
            return true;
          }
        }
      } catch (error) {
        console.log(`Reconnection attempt ${attempt} failed:`, error.message);
      }
      
      // Exponential backoff
      const delay = Math.min(this.retryDelay * Math.pow(2, attempt - 1), this.maxRetryDelay);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    
    console.log('Reconnection failed after all attempts');
    return false;
  }

  /**
   * Trigger reconnect when back online
   */
  async triggerReconnect() {
    // Small delay to ensure network is stable
    setTimeout(async () => {
      await this.attemptReconnection();
    }, 2000);
  }

  /**
   * Add connectivity listener
   */
  addListener(callback) {
    this.listeners.push(callback);
    // Immediately call with current status
    callback({
      isOnline: this.isOnline,
      connectionType: this.connectionType,
    });
    
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  /**
   * Notify all listeners
   */
  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback({
          isOnline: this.isOnline,
          connectionType: this.connectionType,
        });
      } catch (error) {
        console.error('Error in connectivity listener:', error);
      }
    });
  }

  /**
   * Get current connectivity status
   */
  getStatus() {
    return {
      isOnline: this.isOnline,
      connectionType: this.connectionType,
      lastSuccessfulConnection: this.lastSuccessfulConnection,
      consecutiveFailures: this.consecutiveFailures,
    };
  }

  /**
   * Cleanup
   */
  destroy() {
    if (this.connectionCheckInterval) {
      clearInterval(this.connectionCheckInterval);
      this.connectionCheckInterval = null;
    }
    this.listeners = [];
  }
}

// Singleton instance
const connectivityService = new ConnectivityService();

export default connectivityService;

